import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "antd";
import useContainerDimensions from "../../hooks/useContainerDimensions";
import { getWells } from "../../services/api-server/wells";
import { GetAntIcon } from "../../utils/ant_icons";
import { getReportAssetDisplay } from "../../utils/reporting/reportAssetDisplay";

type ReportAssetCardProps = {
	rigName?: string;
	rigImage?: string;
	wellName?: string;
	wellId?: string;
	operatorKey?: string;
	isLoading?: boolean;
	className?: string;
};

function ReportAssetCard({
	rigName,
	rigImage,
	wellName,
	wellId,
	operatorKey,
	isLoading = false,
	className = "",
}: ReportAssetCardProps) {
	const [imageContainerRef, imageContainerDimensions] =
		useContainerDimensions();

	const display = getReportAssetDisplay({
		rigName,
		rigImage,
		wellName,
		wellId,
		operatorKey,
	});

	const { data: wellData, isLoading: isLoadingWell } = useQuery({
		queryKey: ["wells", wellId],
		queryFn: async () => {
			const response: any = await getWells(operatorKey!, { _id: wellId });
			return response[0] ?? null;
		},
		enabled: display.shouldFetchWell,
		staleTime: 5 * 60 * 1000,
	});

	const resolvedWellName = display.wellName ?? wellData?.well_name ?? "N/A";
	const isLoadingImage = isLoading;

	return (
		<div
			className={`flex-1 basis-1/2 min-w-0 flex flex-col bg-white/8 ${className}`}
		>
			<div className="relative w-full h-full">
				<div
					className="
						absolute top-0 left-0 z-20
						rounded-br-2xl rounded-tl-sm
						bg-(--default-btn-color)
						text-(--default-btn-text-color)
						shadow-[0_0_6px_0_rgba(0,0,0,0.30)]
						px-4 h-8 flex items-center gap-2 text-sm
					"
				>
					{GetAntIcon("rig-icon")}
					{display.rigName}
					{GetAntIcon("well-icon")}{" "}
					{display.shouldFetchWell && isLoadingWell ? (
						<Skeleton.Input active={true} size="small" />
					) : (
						resolvedWellName
					)}
				</div>
				<div className="inset-0 w-full h-full">
					{isLoadingImage ? (
						<Skeleton.Image
							active
							style={{
								width: imageContainerDimensions.width,
								height: imageContainerDimensions.height,
							}}
						/>
					) : display.image ? (
						<div
							ref={imageContainerRef}
							className="flex items-center justify-center h-full"
						>
							<img
								src={display.image}
								alt="rig"
								className="w-full h-full object-cover rounded-sm"
							/>
						</div>
					) : (
						<div
							className={`w-full h-full flex items-center justify-center text-6xl rounded-sm ${
								localStorage.getItem("theme") === "dark"
									? "bg-white/10"
									: "bg-gray-300/80"
							}`}
						>
							<span className="opacity-40">{GetAntIcon("image")}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default ReportAssetCard;
